import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { CHART_COLORS } from '../utils/chartTheme';
import '../styles/LaneAnalysis.css';

const fmt = (v, d = 0) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v || 0);
const fmtCur = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

const getRpmClass = (rpm) => {
  if (rpm >= 3) return 'good';
  if (rpm >= 2) return 'ok';
  if (rpm >= 1.5) return 'low';
  return 'bad';
};

const getDhClass = (pct) => {
  if (pct <= 10) return 'low';
  if (pct <= 15) return 'medium';
  return 'high';
};

const getGapClass = (gap) => {
  if (gap > 20) return 'high';
  if (gap > 10) return 'medium';
  return 'low';
};

const getDhBarColor = (pct) => {
  if (pct <= 10) return CHART_COLORS.positive;
  if (pct <= 15) return CHART_COLORS.warning;
  return CHART_COLORS.negative;
};

export default function LaneAnalysis({ selectedCompany, buildParams }) {
  const [laneData, setLaneData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: 'total_pay', dir: 'desc' });
  const [activeView, setActiveView] = useState('table');
  const [exporting, setExporting] = useState(false);
  const lastParamsRef = useRef('');

  const fetchLaneData = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const params = buildParams();
      const res = await api.get(`/loads/${selectedCompany}/lane-summary?${params}`);
      setLaneData(res.data);
    } catch (err) {
      console.error('Failed to fetch lane data:', err);
      setLaneData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, buildParams]);

  useEffect(() => {
    if (!selectedCompany) return;
    const currentParams = buildParams();
    if (currentParams !== lastParamsRef.current) {
      lastParamsRef.current = currentParams;
      fetchLaneData();
    }
  }, [selectedCompany, buildParams, fetchLaneData]);

  useEffect(() => {
    if (selectedCompany) {
      lastParamsRef.current = '';
      fetchLaneData();
    }
  }, [selectedCompany]);

  const sortedLanes = useMemo(() => {
    if (!laneData?.lanes) return [];
    const sorted = [...laneData.lanes].sort((a, b) => {
      const aVal = a[sort.key] ?? 0;
      const bVal = b[sort.key] ?? 0;
      return sort.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [laneData, sort]);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortIcon = (key) => {
    if (sort.key !== key) return <span className="lane-sort-icon inactive">{'\u25B4\u25BE'}</span>;
    return <span className="lane-sort-icon">{sort.dir === 'asc' ? '\u25B4' : '\u25BE'}</span>;
  };

  const handleExport = async () => {
    if (!laneData) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // Lanes sheet
      const lanesSheet = laneData.lanes.map((l) => ({
        'Lane': `${l.pickup_state} → ${l.delivery_state}`,
        'Loads': l.load_count,
        'Total Revenue': l.total_pay,
        'Avg Pay': l.avg_pay,
        'Avg Miles': l.avg_miles,
        'RPM': l.rpm,
        'DH%': l.deadhead_pct,
        'Brokers': l.unique_brokers,
        'Drivers': l.unique_drivers,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lanesSheet), 'Top Lanes');

      // Hotspots sheet
      if (laneData.deadhead_hotspots) {
        const hotspotsSheet = laneData.deadhead_hotspots.map((h) => ({
          'State': h.state,
          'DH%': h.deadhead_pct,
          'Loads': h.load_count,
          'Total Empty Miles': h.total_empty_miles,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hotspotsSheet), 'DH Hotspots');
      }

      XLSX.writeFile(wb, 'lane_analysis.xlsx');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Heatmap helpers
  const heatmapData = useMemo(() => {
    if (!laneData?.state_heatmap) return null;
    const hm = laneData.state_heatmap;
    const pickupStates = [...new Set(hm.map((h) => h.pickup_state))].sort();
    const deliveryStates = [...new Set(hm.map((h) => h.delivery_state))].sort();
    const lookup = {};
    let minRpm = Infinity;
    let maxRpm = -Infinity;
    hm.forEach((h) => {
      const key = `${h.pickup_state}-${h.delivery_state}`;
      lookup[key] = { rpm: h.rpm, load_count: h.load_count };
      if (h.rpm < minRpm) minRpm = h.rpm;
      if (h.rpm > maxRpm) maxRpm = h.rpm;
    });
    return { pickupStates, deliveryStates, lookup, minRpm, maxRpm };
  }, [laneData]);

  const getHeatmapColor = (rpm) => {
    if (!heatmapData || heatmapData.maxRpm === heatmapData.minRpm) return 'hsl(60, 70%, 85%)';
    const ratio = (rpm - heatmapData.minRpm) / (heatmapData.maxRpm - heatmapData.minRpm);
    return `hsl(${ratio * 120}, 70%, 85%)`;
  };

  // Backhaul sorted
  const sortedBackhaul = useMemo(() => {
    if (!laneData?.backhaul_opportunities) return [];
    return [...laneData.backhaul_opportunities].sort((a, b) => b.gap - a.gap);
  }, [laneData]);

  // Deadhead chart data (top 15)
  const dhChartData = useMemo(() => {
    if (!laneData?.deadhead_hotspots) return [];
    return [...laneData.deadhead_hotspots]
      .sort((a, b) => b.deadhead_pct - a.deadhead_pct)
      .slice(0, 15);
  }, [laneData]);

  if (loading) {
    return (
      <div className="lane-analysis">
        <div className="lane-loading">
          <div className="spinner" />
          <p>Loading lane analysis...</p>
        </div>
      </div>
    );
  }

  if (!laneData) {
    return (
      <div className="lane-analysis">
        <div className="lane-empty">No lane data available. Select a company and date range to begin.</div>
      </div>
    );
  }

  return (
    <div className="lane-analysis">
      <div className="lane-view-toggle">
        <button
          className={`lane-view-btn ${activeView === 'table' ? 'active' : ''}`}
          onClick={() => setActiveView('table')}
        >
          Top Lanes
        </button>
        <button
          className={`lane-view-btn ${activeView === 'heatmap' ? 'active' : ''}`}
          onClick={() => setActiveView('heatmap')}
        >
          Lane Heatmap
        </button>
        <button
          className={`lane-view-btn ${activeView === 'hotspots' ? 'active' : ''}`}
          onClick={() => setActiveView('hotspots')}
        >
          DH Hotspots
        </button>
        <button
          className="lane-view-btn lane-export-btn"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {activeView === 'table' && (
        <div className="lane-table-wrapper">
          <div className="lane-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="lane-sortable" onClick={() => handleSort('pickup_state')}>
                    Lane {sortIcon('pickup_state')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('load_count')}>
                    Loads {sortIcon('load_count')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('total_pay')}>
                    Total Revenue {sortIcon('total_pay')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('avg_pay')}>
                    Avg Pay {sortIcon('avg_pay')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('avg_miles')}>
                    Avg Miles {sortIcon('avg_miles')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('rpm')}>
                    RPM {sortIcon('rpm')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('deadhead_pct')}>
                    DH% {sortIcon('deadhead_pct')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('unique_brokers')}>
                    Brokers {sortIcon('unique_brokers')}
                  </th>
                  <th className="lane-sortable" onClick={() => handleSort('unique_drivers')}>
                    Drivers {sortIcon('unique_drivers')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLanes.map((lane, i) => (
                  <tr key={i}>
                    <td>
                      <span className="lane-name">{lane.pickup_state}</span>
                      <span className="lane-arrow">&rarr;</span>
                      <span className="lane-name">{lane.delivery_state}</span>
                    </td>
                    <td>{fmt(lane.load_count)}</td>
                    <td>{fmtCur(lane.total_pay)}</td>
                    <td>{fmtCur(lane.avg_pay)}</td>
                    <td>{fmt(lane.avg_miles)}</td>
                    <td>
                      <span className={`lane-rpm ${getRpmClass(lane.rpm)}`}>
                        {fmt(lane.rpm, 2)}
                      </span>
                    </td>
                    <td>
                      <span className={`lane-dh ${getDhClass(lane.deadhead_pct)}`}>
                        {fmt(lane.deadhead_pct, 1)}%
                      </span>
                    </td>
                    <td>{fmt(lane.unique_brokers)}</td>
                    <td>{fmt(lane.unique_drivers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'heatmap' && heatmapData && (
        <div className="lane-heatmap-wrapper">
          <div className="lane-heatmap-scroll">
            <table className="lane-heatmap">
              <thead>
                <tr>
                  <th className="lane-hm-header">PU \ DEL</th>
                  {heatmapData.deliveryStates.map((ds) => (
                    <th key={ds}>{ds}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapData.pickupStates.map((ps) => (
                  <tr key={ps}>
                    <td className="lane-hm-header">{ps}</td>
                    {heatmapData.deliveryStates.map((ds) => {
                      const cell = heatmapData.lookup[`${ps}-${ds}`];
                      if (!cell) return <td key={ds}></td>;
                      return (
                        <td
                          key={ds}
                          style={{ background: getHeatmapColor(cell.rpm) }}
                        >
                          <span className="lane-hm-cell">
                            {fmt(cell.rpm, 2)}
                            <sub className="lane-hm-count">{cell.load_count}</sub>
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'hotspots' && (
        <div className="lane-hotspots-grid">
          <div className="lane-hotspot-section">
            <div className="lane-hotspot-title">Deadhead Hotspots</div>
            <div className="lane-hotspot-content">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={dhChartData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="state" tick={{ fontSize: 11 }} width={40} />
                  <Tooltip
                    formatter={(value) => [`${fmt(value, 1)}%`, 'Deadhead']}
                    contentStyle={{ fontSize: '0.75rem' }}
                  />
                  <Bar dataKey="deadhead_pct" radius={[0, 3, 3, 0]}>
                    {dhChartData.map((entry, idx) => (
                      <Cell key={idx} fill={getDhBarColor(entry.deadhead_pct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lane-hotspot-section">
            <div className="lane-hotspot-title">
              States where deliveries significantly exceed pickups (backhaul risk)
            </div>
            <div className="lane-hotspot-content">
              <table className="lane-backhaul-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Deliveries</th>
                    <th>Pickups</th>
                    <th>Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBackhaul.map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{row.state}</td>
                      <td>{fmt(row.deliveries)}</td>
                      <td>{fmt(row.pickups)}</td>
                      <td>
                        <span className={`lane-gap-badge ${getGapClass(row.gap)}`}>
                          {fmt(row.gap)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
